import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, User, Mail, Phone, MapPin, Briefcase, DollarSign, FileText, Calendar, Home, GraduationCap, Clock, Target, CheckCircle, Globe } from 'lucide-react';
import { submitLeadToFollowUpBoss, trackCRMSubmission, type LeadData } from '../utils/crmIntegration';
import { trackModalOpen, trackModalClose, trackFormStep, trackButtonClick } from '../utils/gtmTracking';

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string; // Track which button/section opened the modal
}

interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zipCode: string;
  
  // Professional Background
  currentEmployment: string;
  occupation: string;
  yearsExperience: string;
  previousSales: string;
  realEstateExperience: string;
  
  // Land & Outdoor Experience - Updated to arrays for multiple selections
  huntingExperience: string[];
  farmingExperience: string[];
  landRelatedExperience: string;
  
  // Goals & Requirements
  whyKiloterra: string;
  heardAboutKiloterra: string;
  
  // Requirements Acknowledgments
  fullTimeCommitment: boolean;
  realEstateLicense: boolean;
  commissionBased: boolean;
  independentContractor: boolean;
  businessExpenses: boolean;
  toolsEquipment: boolean;
  noAssignedTerritories: boolean;
  
  // Readiness Assessment
  readinessStatus: string;
  
  // Additional Information
  additionalInfo: string;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({ isOpen, onClose, source = 'unknown' }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    county: '',
    state: '',
    zipCode: '',
    currentEmployment: '',
    occupation: '',
    yearsExperience: '',
    previousSales: '',
    realEstateExperience: '',
    huntingExperience: [],
    farmingExperience: [],
    landRelatedExperience: '',
    whyKiloterra: '',
    heardAboutKiloterra: '',
    fullTimeCommitment: false,
    realEstateLicense: false,
    commissionBased: false,
    independentContractor: false,
    businessExpenses: false,
    toolsEquipment: false,
    noAssignedTerritories: false,
    readinessStatus: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 4;

  // Track modal open
  React.useEffect(() => {
    if (isOpen) {
      // GTM tracking
      trackModalOpen('application_modal', source);
    }
  }, [isOpen, source]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle checkbox changes for multi-select fields
  const handleCheckboxChange = (fieldName: keyof FormData, value: string) => {
    const currentValues = formData[fieldName] as string[];
    const updatedValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];
    
    setFormData({
      ...formData,
      [fieldName]: updatedValues
    });
  };

  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleStepChange = (newStep: number) => {
    // Validate current step before advancing
    if (newStep > currentStep && !validateCurrentStep()) {
      return;
    }
    
    // GTM tracking for form steps
    trackFormStep('application', newStep, totalSteps);
    
    setCurrentStep(newStep);
    
    // Scroll to top after step change
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  // Validation function for each step
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!(
          formData.firstName.trim() &&
          formData.lastName.trim() &&
          formData.email.trim() &&
          formData.phone.trim() &&
          formData.address.trim() &&
          formData.city.trim() &&
          formData.county.trim() &&
          formData.state &&
          formData.zipCode.trim() &&
          formData.heardAboutKiloterra
        );
      case 2:
        return !!(
          formData.currentEmployment &&
          formData.yearsExperience
        );
      case 3:
        return hasHuntingExperience && hasFarmingExperience;
      case 4:
        return !!(
          formData.whyKiloterra.trim() &&
          allRequirementsAcknowledged &&
          formData.readinessStatus
        );
      default:
        return true;
    }
  };

  // Get validation error message for current step
  const getValidationMessage = (): string => {
    switch (currentStep) {
      case 1:
        return 'Please fill in all required personal information fields.';
      case 2:
        return 'Please fill in all required professional background fields.';
      case 3:
        return 'Please select at least one option for both hunting and farming experience.';
      case 4:
        return 'Please complete all required fields and acknowledge all requirements.';
      default:
        return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create a comprehensive but clean application summary
      const applicationSummary = `KILOTERRA Career Application Summary

CONTACT INFORMATION:
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}
Address: ${formData.address}, ${formData.city}, ${formData.county}, ${formData.state} ${formData.zipCode}

PROFESSIONAL BACKGROUND:
Current Employment: ${formData.currentEmployment}
Occupation: ${formData.occupation || 'Not specified'}
Years of Experience: ${formData.yearsExperience}
Previous Sales Experience: ${formData.previousSales || 'Not specified'}
Real Estate Experience: ${formData.realEstateExperience || 'Not specified'}

LAND & OUTDOOR EXPERIENCE:
Hunting Experience: ${formData.huntingExperience.length > 0 ? formData.huntingExperience.join(', ') : 'None specified'}
Farming Experience: ${formData.farmingExperience.length > 0 ? formData.farmingExperience.join(', ') : 'None specified'}
Land Related Experience: ${formData.landRelatedExperience || 'None provided'}

MOTIVATION & GOALS:
Why KILOTERRA: ${formData.whyKiloterra}
How they heard about us: ${formData.heardAboutKiloterra}

REQUIREMENTS ACKNOWLEDGMENT:
✓ Full-time Commitment: ${formData.fullTimeCommitment ? 'Acknowledged' : 'Not acknowledged'}
✓ Real Estate License: ${formData.realEstateLicense ? 'Acknowledged' : 'Not acknowledged'}
✓ Commission-based Role: ${formData.commissionBased ? 'Acknowledged' : 'Not acknowledged'}
✓ Independent Contractor: ${formData.independentContractor ? 'Acknowledged' : 'Not acknowledged'}
✓ Business Expenses: ${formData.businessExpenses ? 'Acknowledged' : 'Not acknowledged'}
✓ Tools & Equipment: ${formData.toolsEquipment ? 'Acknowledged' : 'Not acknowledged'}
✓ No Assigned Territories: ${formData.noAssignedTerritories ? 'Acknowledged' : 'Not acknowledged'}

READINESS STATUS: ${formData.readinessStatus}

ADDITIONAL INFORMATION: ${formData.additionalInfo || 'None provided'}

Application Source: ${source}
Submission Date: ${new Date().toLocaleString()}`;

      // Prepare lead data for analytics tracking
      const leadData: LeadData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: applicationSummary,
        source: 'Careers - Application', // Added "Careers" prefix
        leadType: 'application',
        timestamp: new Date().toISOString(),
        
        // Include all form fields for analytics
        address: formData.address,
        city: formData.city,
        county: formData.county,
        state: formData.state,
        zipCode: formData.zipCode,
        currentEmployment: formData.currentEmployment,
        occupation: formData.occupation,
        yearsExperience: formData.yearsExperience,
        previousSales: formData.previousSales,
        realEstateExperience: formData.realEstateExperience,
        huntingExperience: formData.huntingExperience,
        farmingExperience: formData.farmingExperience,
        landRelatedExperience: formData.landRelatedExperience,
        whyKiloterra: formData.whyKiloterra,
        heardAboutKiloterra: formData.heardAboutKiloterra,
        readinessStatus: formData.readinessStatus,
        additionalInfo: formData.additionalInfo
      };

      // Submit form (analytics only)
      const success = await submitLeadToFollowUpBoss(leadData);
      
      // Track the submission
      trackCRMSubmission(leadData, success);

      console.log('Application submitted successfully:', { 
        applicant: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        source,
        redirecting: 'to thank-you page'
      });
      
      // Close modal and redirect to thank you page
      onClose();
      navigate('/thank-you?type=application&source=' + source);
      
    } catch (error) {
      console.error('Application submission error:', error);
      
      // Still redirect to thank you page even if there's an error
      onClose();
      navigate('/thank-you?type=application&source=' + source);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // GTM tracking
      trackModalClose('application_modal', false);
      
      // Reset form state
      setCurrentStep(1);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        county: '',
        state: '',
        zipCode: '',
        currentEmployment: '',
        occupation: '',
        yearsExperience: '',
        previousSales: '',
        realEstateExperience: '',
        huntingExperience: [],
        farmingExperience: [],
        landRelatedExperience: '',
        whyKiloterra: '',
        heardAboutKiloterra: '',
        fullTimeCommitment: false,
        realEstateLicense: false,
        commissionBased: false,
        independentContractor: false,
        businessExpenses: false,
        toolsEquipment: false,
        noAssignedTerritories: false,
        readinessStatus: '',
        additionalInfo: ''
      });
      onClose();
    }
  };

  // Check if all requirements are acknowledged
  const allRequirementsAcknowledged = 
    formData.fullTimeCommitment &&
    formData.realEstateLicense &&
    formData.commissionBased &&
    formData.independentContractor &&
    formData.businessExpenses &&
    formData.toolsEquipment &&
    formData.noAssignedTerritories;

  // Check if hunting and farming experience are selected (mandatory)
  const hasHuntingExperience = formData.huntingExperience.length > 0;
  const hasFarmingExperience = formData.farmingExperience.length > 0;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-kiloterra-dark mb-2 uppercase">Personal Information</h3>
              <p className="text-gray-600">Let's start with the basics</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  First Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                    placeholder="First name"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Street Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Home className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="123 Main Street"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="City"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  County <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                    placeholder="County"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                >
                  <option value="">Select State</option>
                  <option value="IA">Iowa</option>
                  <option value="MO">Missouri</option>
                  <option value="NE">Nebraska</option>
                  <option value="KS">Kansas</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="12345"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                How did you hear about KILOTERRA? <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <select
                  name="heardAboutKiloterra"
                  value={formData.heardAboutKiloterra}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                >
                  <option value="">Select Option</option>
                  <option value="social-media">Social Media</option>
                  <option value="personal-connection">Personal Connection</option>
                  <option value="google">Google</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="indeed">Indeed</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-kiloterra-dark mb-2 uppercase">Professional Background</h3>
              <p className="text-gray-600">Tell us about your work experience</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Current Employment Status <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <select
                  name="currentEmployment"
                  value={formData.currentEmployment}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                >
                  <option value="">Select Employment Status</option>
                  <option value="employed">Currently Employed</option>
                  <option value="unemployed">Currently Unemployed</option>
                  <option value="self-employed">Self-Employed</option>
                  <option value="retired">Retired</option>
                  <option value="student">Student</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">Current/Previous Occupation or Field of Work</label>
              <div className="relative">
                <GraduationCap className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="e.g., Construction, Agriculture, Sales, Education, etc."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Years of Work Experience <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <select
                  name="yearsExperience"
                  value={formData.yearsExperience}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                >
                  <option value="">Select Experience Level</option>
                  <option value="0-2">0-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">Previous Sales Experience</label>
              <div className="relative">
                <Target className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <select
                  name="previousSales"
                  value={formData.previousSales}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                >
                  <option value="">Select Sales Experience</option>
                  <option value="none">No Sales Experience</option>
                  <option value="1-2">1-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Real Estate Experience <span className="text-gray-500 font-normal">(Prior Experience Not Required)</span>
              </label>
              <div className="relative">
                <Home className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <select
                  name="realEstateExperience"
                  value={formData.realEstateExperience}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                >
                  <option value="">Select Real Estate Experience</option>
                  <option value="none">No Real Estate Experience</option>
                  <option value="licensed">Currently Licensed Agent</option>
                  <option value="previously-licensed">Previously Licensed</option>
                  <option value="investor">Real Estate Investor</option>
                  <option value="related">Related Field Experience</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-kiloterra-dark mb-2 uppercase">Land & Outdoor Experience</h3>
              <p className="text-gray-600">Share your connection to the land (select all that apply)</p>
            </div>

            {/* Hunting Experience - Now Mandatory */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4 uppercase">
                Hunting Experience <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'no-experience', label: 'No Hunting Experience' },
                  { value: 'some-experience', label: 'Some Hunting Experience' },
                  { value: 'experienced', label: 'Experienced Hunter' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 p-3 border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.huntingExperience.includes(option.value)}
                      onChange={() => handleCheckboxChange('huntingExperience', option.value)}
                      className="h-4 w-4 text-kiloterra-yellow focus:ring-kiloterra-dark border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Farming Experience - Now Mandatory */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4 uppercase">
                Farming Experience <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'no-experience', label: 'No Farming Experience' },
                  { value: 'some-experience', label: 'Some Farming Experience' },
                  { value: 'experienced', label: 'Experienced Farmer' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 p-3 border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.farmingExperience.includes(option.value)}
                      onChange={() => handleCheckboxChange('farmingExperience', option.value)}
                      className="h-4 w-4 text-kiloterra-yellow focus:ring-kiloterra-dark border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Land Related Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">Land Related Experience</label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <textarea
                  name="landRelatedExperience"
                  value={formData.landRelatedExperience}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="Briefly describe your experience with hunting, farming, land management, and land ownership."
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-kiloterra-dark mb-2 uppercase">Motivation & Requirements</h3>
              <p className="text-gray-600">Help us understand your goals and confirm your understanding of our requirements</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                Why do you want to join KILOTERRA? <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <textarea
                  name="whyKiloterra"
                  value={formData.whyKiloterra}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="Tell us what motivates you to pursue a career in land sales with KILOTERRA..."
                />
              </div>
            </div>

            {/* Requirements Section */}
            <div className="bg-gray-50 p-6">
              <h4 className="text-lg font-bold text-kiloterra-dark mb-4 uppercase">
                Position Requirements - Please acknowledge each requirement <span className="text-red-500">*</span>
              </h4>
              <p className="text-sm text-gray-600 mb-6">
                Please acknowledge all requirements and select your readiness to start a career with KILOTERRA.
              </p>
              <div className="space-y-4">
                {[
                  {
                    key: 'fullTimeCommitment',
                    text: 'I acknowledge that KILOTERRA requires a full-time commitment to be considered for a Land Specialist Position.'
                  },
                  {
                    key: 'realEstateLicense',
                    text: 'If hired, I will be required to obtain a Real Estate License for the state(s) in which I am applying.'
                  },
                  {
                    key: 'commissionBased',
                    text: 'I understand this is a commission-based 1099 independent contractor role, and I will not receive a salary from KILOTERRA.'
                  },
                  {
                    key: 'independentContractor',
                    text: 'KILOTERRA provides training, marketing, and tools, but it is up to each agent to commit to the work to achieve results.'
                  },
                  {
                    key: 'businessExpenses',
                    text: 'As an Independent Contractor, I will be responsible for my own business expenses, retirement plans, and job tools.'
                  },
                  {
                    key: 'toolsEquipment',
                    text: 'I understand that I must have certain tools and equipment including: laptop, scanner, digital camera, valid driver\'s license (required immediately), and 5-year or newer pickup truck and side-by-side (required within first 18 months of hire).'
                  },
                  {
                    key: 'noAssignedTerritories',
                    text: 'I understand there are no assigned territories within KILOTERRA, and there is enough business for everyone to be successful.'
                  }
                ].map((requirement) => (
                  <label key={requirement.key} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name={requirement.key}
                      checked={formData[requirement.key as keyof FormData] as boolean}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-kiloterra-yellow focus:ring-kiloterra-dark border-gray-300 mt-1 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">{requirement.text}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Readiness Assessment */}
            <div className="bg-white border-2 border-kiloterra-dark p-6">
              <h4 className="text-lg font-bold text-kiloterra-dark mb-4 uppercase">
                Readiness Assessment <span className="text-red-500">*</span>
              </h4>
              <p className="text-gray-600 mb-4">Based on the requirements above, please select the option that best describes your current situation:</p>
              
              <div className="space-y-3">
                {[
                  {
                    value: 'ready-now',
                    label: 'I am ready to start an exciting career in land sales',
                    description: 'I currently meet all requirements and am ready to begin immediately'
                  },
                  {
                    value: 'ready-soon',
                    label: 'I do not currently meet all requirements, but expect to meet them soon',
                    description: 'I am working toward meeting the requirements and will be ready within 3-6 months'
                  },
                  {
                    value: 'future-consideration',
                    label: 'I would like to be considered in the future',
                    description: 'I am interested but do not currently meet the requirements'
                  }
                ].map((option) => (
                  <label key={option.value} className="flex items-start space-x-3 p-4 border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="readinessStatus"
                      value={option.value}
                      checked={formData.readinessStatus === option.value}
                      onChange={handleInputChange}
                      required
                      className="h-4 w-4 text-kiloterra-yellow focus:ring-kiloterra-dark border-gray-300 mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-kiloterra-dark">{option.label}</div>
                      <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">Additional Information</label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent"
                  placeholder="Anything else you'd like us to know about you or your interest in land sales?"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          ref={modalContentRef}
          className="relative bg-white w-full max-w-4xl transform transition-all duration-300 scale-100 opacity-100 shadow-2xl max-h-[90vh] overflow-y-auto"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className="bg-kiloterra-dark px-6 py-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/kiloterra_logo_stacked_simple_grey.png" 
                alt="KILOTERRA Logo" 
                className="h-8 w-auto brightness-0 invert"
              />
            </div>
            <h2 className="text-3xl font-bold text-white uppercase">
              Join Our Team
            </h2>
            <p className="text-gray-200 mt-2">
              Take the first step toward a career in Land Sales with KILOTERRA
            </p>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-300 uppercase">Step {currentStep} of {totalSteps}</span>
                <span className="text-sm text-gray-300">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-600 h-2">
                <div 
                  className="bg-kiloterra-yellow h-2 transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="px-6 py-8">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  trackButtonClick('Previous Step', `application_step_${currentStep}`, `step_${currentStep - 1}`);
                  handleStepChange(currentStep - 1);
                }}
                disabled={currentStep === 1 || isSubmitting}
                className="px-6 py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed uppercase font-medium"
              >
                Previous
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={() => {
                    if (validateCurrentStep()) {
                      trackButtonClick('Next Step', `application_step_${currentStep}`, `step_${currentStep + 1}`);
                      handleStepChange(currentStep + 1);
                    }
                  }}
                  disabled={isSubmitting}
                  className={`px-8 py-3 font-bold transition-all duration-300 transform uppercase tracking-wide ${
                    validateCurrentStep()
                      ? 'bg-kiloterra-yellow text-kiloterra-dark hover:bg-opacity-90 hover:scale-105'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={() => trackButtonClick('Submit Application', 'application_step_4', 'form_submission')}
                  disabled={isSubmitting || !allRequirementsAcknowledged || !formData.readinessStatus}
                  className="bg-kiloterra-yellow text-kiloterra-dark px-8 py-3 font-bold hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wide"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>

            {/* Validation Warning */}
            {!validateCurrentStep() && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {getValidationMessage()}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;